<template>
  <div class="container-fluid">
    <!--
      Dashboard por widgets (cada usuario elige, mueve y redimensiona los suyos).
      Los bloques de siempre se pasan por slot y siguen usando los datos de acá.
    -->
    <DashboardWidgetGrid
      :is-duntower="isDuntower"
      :show-legal-shortcuts="showJurisprudenciaDashboardShortcuts"
    >
      <!-- Los avisos son de todo el dashboard, no del calendario: van junto a «Widgets».
           En escritorio el icono de la IA va acá en vez de flotar (en el teléfono está en el encabezado). -->
      <template #toolbar>
        <AgentAiTopButton v-if="!isMobileLayout" inline />
        <NotificationPreferencesButton />
      </template>

      <template #widget-shortcuts>
        <div
          v-if="showJurisprudenciaDashboardShortcuts"
          class="row mb-3 gx-3 jurisprudencia-dashboard-shortcuts"
        >
          <div
            v-if="jurisprudenciaShortcutVisible"
            class="col-md-6 mb-3 mb-md-0"
          >
            <router-link
              class="text-decoration-none text-body"
              to="/jurisprudencia"
            >
              <div class="card h-100 border shadow-sm dashboard-shortcut-card">
                <div class="card-body d-flex gap-3">
                  <div
                    class="dashboard-shortcut-icon rounded d-flex align-items-center justify-content-center flex-shrink-0"
                    aria-hidden="true"
                  >
                    <i class="material-icons text-primary">gavel</i>
                  </div>
                  <div class="min-w-0">
                    <h3 class="h5 mb-2 fw-semibold">
                      {{ $t("jurisprudencia.dashboardShortcutTitle") }}
                    </h3>
                    <p class="text-muted small mb-2 mb-md-3">
                      {{ $t("jurisprudencia.dashboardShortcutBody") }}
                    </p>
                    <span class="text-primary small fw-semibold">
                      {{ $t("jurisprudencia.dashboardShortcutCta") }}
                      <i class="material-icons align-middle" style="font-size: 18px"
                        >arrow_forward</i
                      >
                    </span>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
          <div v-if="mevShortcutVisible" class="col-md-6">
            <router-link class="text-decoration-none text-body" to="/mev">
              <div class="card h-100 border shadow-sm dashboard-shortcut-card">
                <div class="card-body d-flex gap-3">
                  <div
                    class="dashboard-shortcut-icon rounded d-flex align-items-center justify-content-center flex-shrink-0"
                    aria-hidden="true"
                  >
                    <i class="material-icons text-primary">assignment</i>
                  </div>
                  <div class="min-w-0">
                    <h3 class="h5 mb-2 fw-semibold">
                      {{ $t("mev.dashboardShortcutTitle") }}
                    </h3>
                    <p class="text-muted small mb-2 mb-md-3">
                      {{ $t("mev.dashboardShortcutBody") }}
                    </p>
                    <span class="text-primary small fw-semibold">
                      {{ $t("mev.dashboardShortcutCta") }}
                      <i class="material-icons align-middle" style="font-size: 18px"
                        >arrow_forward</i
                      >
                    </span>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </template>

      <template #widget-actions-calendar>
        <!--
          «My items» (ticket #424): el mismo filtro que el botón del listado
          (`ownerScope=mine`). Arranca prendido salvo para admin.
        -->
        <MyItemsButton
          v-if="canUseMyItemsFilter"
          :active="isMyItemsFilterActive"
          @toggle="toggleMyItemsFilter"
        />
        <CalendarSync />
        <button
          class="btn btn-sm btn-outline-secondary"
          @click="openCalendarSettings"
          title="Configurar campos del calendario"
        >
          <i class="material-icons">settings</i>
        </button>
      </template>

      <template #widget-calendar>
        <div
          class="row dashboard-layout"
          :class="{ 'calendar-contracted': isCalendarContracted }"
        >
          <!-- Lista de eventos del día seleccionado (a la izquierda) -->
          <transition name="slide-left" appear>
            <div v-if="isCalendarContracted" class="events-list-section">
              <div class="events-list-container">
                <div class="events-list-header">
                  <h6 class="mb-1">
                    <i class="material-icons mr-2">event</i>
                    {{ selectedDate }}
                  </h6>
                  <p
                    v-if="highlightedEventDateTime"
                    class="text-muted small mb-3"
                  >
                    {{ highlightedEventDateTime }}
                  </p>
                  <!--
                    El alta de eventos propios del calendario se sacó (ticket #365): la
                    agenda interna no se usó nunca en Duntower y la idea es que cada uno
                    lleve la suya en su calendario personal, suscrito al feed del sync.
                  -->
                  <div class="d-flex flex-wrap gap-2 align-items-center">
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      @click="expandCalendar"
                    >
                      <i class="material-icons mr-1">close</i>
                    </button>
                  </div>
                </div>
                <ContentSkeleton
                  v-if="loading.calendarEvents"
                  variant="list"
                  :rows="3"
                  padded
                />
                <div
                  v-else-if="selectedDayEvents.length === 0"
                  class="text-center py-4 rounded events-empty-state"
                >
                  <i class="material-icons" style="font-size: 48px; opacity: 0.45"
                    >event_busy</i
                  >
                  <p class="text-muted mt-2 mb-0">
                    {{ $t("calendar.noEventsForDate") }}
                  </p>
                </div>
                <div v-else>
                  <div class="events-count mb-3">
                    <span class="badge badge-info"
                      >{{ selectedDayEvents.length }} {{ $t("events") }}</span
                    >
                  </div>
                  <div class="events-list">
                    <div
                      v-for="event in selectedDayEvents"
                      :key="event._id"
                      class="event-item"
                      :class="{ 'event-item--highlighted': isHighlightedCalendarEvent(event) }"
                    >
                      <div
                        v-if="formatCalendarEventDateTime(event)"
                        class="calendar-event-datetime text-muted small mb-2 w-100"
                      >
                        <i
                          class="material-icons align-middle mr-1"
                          style="font-size: 16px"
                          >schedule</i
                        >
                        {{ formatCalendarEventDateTime(event) }}
                      </div>
                      <!-- Payment Event -->
                      <div
                        v-if="event.modelName === 'payment'"
                        class="event-content"
                      >
                        <div class="event-header">
                          <div class="d-flex align-items-center justify-content-between mb-2">
                            <h6
                              class="event-title clickable mb-0"
                              @click="viewItem(event)"
                            >
                              <i :class="getEventTypeIcon(event.eventTypeInfo)" :style="{ color: getEventColor(event) }" class="me-2"></i>
                              {{ event.title || $t("Payment") }}
                            </h6>
                            <span
                              :class="['badge', getEventTypeBadgeClass(event)]"
                              :style="getEventBadgeStyle(event)"
                            >
                              {{ event.eventType }} 
                            </span> 
                          </div>
                          <div class="event-meta">
                            <div class="text-danger fw-bold">
                              ${{ formatNumber(event.item.amount || 0) }}
                            </div>
                            {{ $t('Payment to') }}:

                            <div class="text-primary">
                             {{ event.item.contact?.name || $t("N/A") }}
                            </div>
                            {{ $t('Bank account') }}:
                            <div class="text-muted">  {{ event.item.bankaccount?.name || $t("N/A") }}
                            </div>
                            <div
                              :class="
                                event.item.paid ? 'text-success' : 'text-danger'
                              "
                            >
                              {{ event.item.paid ? $t("Paid") : $t("Not paid") }}
                            </div>
                            <div v-if="event.item.comment" class="text-muted small">
                              {{ event.item.comment }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Collect Event -->
                      <div
                        v-else-if="event.modelName === 'collect'"
                        class="event-content"
                      >
                        <div class="event-header">
                          <div class="d-flex align-items-center justify-content-between mb-2">
                            <h6
                              class="event-title clickable mb-0"
                              @click="viewItem(event)"
                            >
                              <i :class="getEventTypeIcon(event.eventTypeInfo)" :style="{ color: getEventColor(event) }" class="me-2"></i>
                              {{ event.title || $t("Collection") }}
                            </h6>
                            <span
                              :class="['badge', getEventTypeBadgeClass(event)]"
                              :style="getEventBadgeStyle(event)"
                            >
                              {{ event.eventType }}
                            </span>
                          </div>
                          <div class="event-meta">
                            <div class="text-success fw-bold">
                              ${{ formatNumber(event.item.amount || 0) }}
                            </div>
                            <div class="text-primary">
                              {{ event.item.contact?.name || $t("N/A") }}
                            </div>
                            <div class="text-muted">
                              {{ event.item.bankaccount?.name || $t("N/A") }}
                            </div>
                            <div
                              :class="
                                event.item.received
                                  ? 'text-success'
                                  : 'text-warning'
                              "
                            >
                              {{
                                event.item.received ? $t("Received") : $t("Pending")
                              }}
                            </div>
                            <div v-if="event.item.comment" class="text-muted small">
                              {{ event.item.comment }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Shipping Event -->
                      <div
                        v-else-if="event.modelName === 'shipping'"
                        class="event-content"
                      >
                        <div class="event-header">
                          <div class="d-flex align-items-center justify-content-between mb-2">
                            <h6
                              class="event-title clickable mb-0"
                              @click="viewItem(event)"
                            >
                              <i :class="getEventTypeIcon(event.eventTypeInfo)" :style="{ color: getEventColor(event) }" class="me-2"></i>
                              {{ event.title || $t("Shipping") }}
                            </h6>
                            <span
                              :class="['badge', getEventTypeBadgeClass(event)]"
                              :style="getEventBadgeStyle(event)"
                            >
                              {{ event.eventType }}
                            </span>
                          </div>
                          <div class="event-meta">
                            <div class="text-primary">
                              {{ event.item.client?.name || $t("N/A") }}
                            </div>
                            <div class="text-info fw-bold">
                              ${{ formatNumber(event.item.total_proform || 0) }}
                            </div>
                            <div class="text-muted">
                              {{ event.item.shipper?.name || $t("N/A") }}
                            </div>
                            <div>
                              <ShippingStatusBadge 
                                :item="event.item" 
                                :model-name="event.modelName"
                                size="small"
                              />
                            </div>
                            <div
                              v-if="event.item.comments_invoice"
                              class="text-muted small"
                            >
                              {{ event.item.comments_invoice }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Proform Event -->
                      <div
                        v-else-if="event.modelName === 'proform'"
                        class="event-content"
                      >
                        <div class="event-header">
                          <div class="d-flex align-items-center justify-content-between mb-2">
                            <h6
                              class="event-title clickable mb-0"
                              @click="viewItem(event)"
                            >
                              <i :class="getEventTypeIcon(event.eventTypeInfo)" :style="{ color: getEventColor(event) }" class="me-2"></i>
                              {{ event.title || $t("Proform") }}
                            </h6>
                            <span
                              :class="['badge', getEventTypeBadgeClass(event)]"
                              :style="getEventBadgeStyle(event)"
                            >
                              {{ event.eventType }}
                            </span>
                          </div>
                          <div class="event-meta">
                            <div class="text-primary">
                              {{ event.item.client?.name || $t("N/A") }}
                            </div>
                            <div class="text-warning fw-bold">
                              ${{ formatNumber(event.item.total_proform || 0) }}
                            </div>
                            <div class="text-muted">
                              {{ event.item.paymentConditionLabel || $t("N/A") }}
                            </div>
                            <div :class="getProformStatusClass(event.item.status)">
                              {{ getProformStatusLabel(event.item.status) }}
                            </div>
                            <div
                              v-if="event.item.description"
                              class="text-muted small"
                            >
                              {{ event.item.description }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Expense Event -->
                      <div
                        v-else-if="event.modelName === 'expense'"
                        class="event-content"
                      >
                        <div class="event-header">
                          <div class="d-flex align-items-center justify-content-between mb-2">
                            <h6
                              class="event-title clickable mb-0"
                              @click="viewItem(event)"
                            >
                              <i :class="getEventTypeIcon(event.eventTypeInfo)" :style="{ color: getEventColor(event) }" class="me-2"></i>
                              {{ event.title || $t("Expense") }}
                            </h6>
                            <span
                              :class="['badge', getEventTypeBadgeClass(event)]"
                              :style="getEventBadgeStyle(event)"
                            >
                              {{ event.eventType }}
                            </span>
                          </div>
                          <div class="event-meta">
                            <div class="text-danger fw-bold">
                              ${{ formatNumber(event.item.amount || 0) }}
                            </div>
                            <div class="text-primary">
                              {{ event.item.contact?.name || $t("N/A") }}
                            </div>
                            <div class="text-muted">
                              {{ event.item.type || $t("N/A") }}
                            </div>
                            <div
                              :class="
                                event.item.paid ? 'text-success' : 'text-warning'
                              "
                            >
                              {{ event.item.paid ? $t("Paid") : $t("Pending") }}
                            </div>
                            <div
                              v-if="event.item.comments"
                              class="text-muted small"
                            >
                              {{ event.item.comments }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Default Event (Generic) -->
                      <div v-else class="event-content">
                        <div class="event-header">
                          <div class="d-flex align-items-center justify-content-between mb-2">
                            <h6
                              class="event-title clickable mb-0"
                              @click="viewItem(event)"
                            >
                              <i :class="getEventTypeIcon(event.eventTypeInfo)" :style="{ color: getEventColor(event) }" class="me-2"></i>
                              {{ event.title || $t(`Item ${event._id}`) }}
                            </h6>
                            <span
                              :class="['badge', getEventTypeBadgeClass(event)]"
                              :style="getEventBadgeStyle(event)"
                            >
                              {{ event.eventType }} 
                            </span>
                          </div>
                          <div class="event-meta">
                            <div class="text-muted">{{ event.modelLabel }}</div>
                            <div
                              v-if="event.item.description"
                              class="text-muted small"
                            >
                              {{ event.item.description }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>

          <!-- Calendario -->
          <div
            class="calendar-section"
            :class="{ contracted: isCalendarContracted }"
          >
            <ContentSkeleton v-if="loading.calendarEvents" variant="calendar" />
            <Calendar
              v-else
              :events="calendarEvents"
              :mark-collected-shippings="usesShippingStatusColors"
              @day-clicked="showProformDetails"
              @month-changed="handleMonthChange"
            />
          </div>
        </div>
      </template>

      <template #widget-recent-activity>
        <RecentActivity class="dashboard-recent-activity" :limit="25" />
      </template>

      <template #widget-operations-summary>
        <ContentSkeleton
          v-if="loading.operationsSummary"
          variant="dashboard"
          :cols="4"
        />
        <OperationsSummary
          v-else
          class="dashboard-operations-summary"
          :model-data="modelData"
          :calendar-models="calendarModels"
          :calendar-preferences="calendarPreferences"
          :apply-field-status-rules="appliesFieldStatusRules"
        />
      </template>
    </DashboardWidgetGrid>

    <!-- Modal para mostrar detalles de eventos del calendario -->
    <div class="modal fade" id="proformModal" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t("Events for") }} {{ selectedDate }}</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="selectedProforms.length === 0" class="text-center">
              {{ $t("No events for this date") }}
            </div>
            <div v-else class="list-group">
              <div
                v-for="event in selectedProforms"
                :key="event._id"
                class="list-group-item"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="mb-1">
                      {{ event.title || $t(`Item ${event._id}`) }}
                    </h6>
                    <small class="text-muted">
                      {{ $t("Modelo") }}: {{ $t(event.modelLabel) }} | {{ $t("Campo") }}: {{ $t(event.field) }}
                    </small>
                  </div>
                  <router-link
                    :to="event.route"
                    class="btn btn-primary btn-sm"
                  >
                    {{ $t("View details") }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de configuración del calendario -->
    <div
      v-if="showCalendarSettings"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5)"
      @click.self="showCalendarSettings = false"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="d-flex justify-content-between align-items-center w-100">
              <h5 class="modal-title mb-0">
                <i class="material-icons me-2">settings</i>
                {{ $t("Calendar Fields Configuration") }}
              </h5>
              <div class="d-flex align-items-center">
                <button
                  type="button"
                  class="btn-close"
                  @click="showCalendarSettings = false"
                  aria-label="Close"
                ></button>
              </div>
            </div>
          </div>

          <div class="modal-body">
            <div class="mb-2">
              <span class="badge bg-secondary">
                <i class="fas fa-check me-1"></i>
                {{ selectedCalendarFieldsCount }} {{ $t("selected fields") }}
              </span>
            </div>
            <!-- Mismo diseño de switches que «Mis notificaciones» (campanita). -->
            <label class="cal-field-row cal-field-row--all" for="select-all-calendar-fields">
              <span class="fw-semibold">{{ $t("Select all") }}</span>
              <span class="form-check form-switch m-0">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="select-all-calendar-fields"
                  :checked="areAllCalendarFieldsSelected"
                  @change="handleToggleAllCalendarFields"
                />
              </span>
            </label>
            <div v-if="availableCalendarFields.length === 0" class="text-center text-muted py-4">
              {{ $t("No calendar fields available") }}
            </div>
            <div v-else>
              <label
                v-for="field in availableCalendarFields"
                :key="field.id"
                class="cal-field-row"
                :for="`calendar-field-${field.id}`"
              >
                <span>
                  <strong>{{ field.modelLabel || field.modelName }}</strong> - {{ field.label }}
                  <ColumnFieldHelpIcon
                    v-if="field.helpKey"
                    class="calendar-field-help ms-1"
                    :tooltip="$t(field.helpKey)"
                    :aria-label="$t(field.helpKey)"
                    popover-prefer-right
                  />
                </span>
                <span class="form-check form-switch m-0">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="`calendar-field-${field.id}`"
                    v-model="tempCalendarPreferences[field.id]"
                  />
                </span>
              </label>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="cancelCalendarSettings"
            >
              {{ $t("Cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="applyCalendarSettings"
            >
              {{ $t("Apply") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Calendar from "@/components/gotrading/Calendar.vue";
import RecentActivity from "@/components/gotrading/RecentActivity.vue";
import OperationsSummary from "@/components/gotrading/OperationsSummary.vue";
import ShippingStatusBadge from "@/components/gotrading/common/ShippingStatusBadge.vue";
import MyItemsButton from "@/components/gotrading/common/MyItemsButton.vue";
import CalendarSync from "@/components/gotrading/calendar_sync/CalendarSync.vue";
import NotificationPreferencesButton from "@/components/gotrading/common/NotificationPreferencesButton.vue";
import { isDuntowerTenant } from "@/utils/dynamicModelValidationSwal";
import {
  getShippingStatusClass,
  getShippingStatusColor,
  getShippingStatusColors,
  getStatusLabel,
  isShippingClientPendingSettled,
  SHIPPING_COLLECTED_CLASS,
} from "@/utils/shippingStatus";
import {
  calendarTodayLocalYyyyMmDd,
  toLocalYyyyMmDd,
  toYyyyMmDdSync,
} from "@/utils/dateFormatter";
import { resolveValidUserOwnerFieldKeys } from "@/utils/userOwnerField";
import {
  calendarEventRoute,
  dashboardFieldHelpKey,
  FILTERABLE_SHIPPING_STATUSES,
  isHiddenDashboardField,
  isShippingStatusHidden,
  matchesFieldStatusRule,
  resolveEventTypeInfo,
  shippingStatusHelpKey,
  shippingStatusPreferenceId,
  usesCalendarEndpointTenant,
} from "@/utils/dashboardEventTypes";
import ColumnFieldHelpIcon from "@/components/gotrading/common/ColumnFieldHelpIcon.vue";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import DashboardWidgetGrid from "@/components/gotrading/dashboard/DashboardWidgetGrid.vue";
import mobileLayoutMixin from "@/mixins/mobileLayoutMixin";

/**
 * Meses que se traen alrededor de hoy además del mes visible del calendario.
 *
 * Cubren la ventana de los bloques de la derecha y del resumen (vencidos recientes,
 * hoy, esta semana y próximos 30 días), que leen `modelData` igual que el calendario.
 */
const PANEL_MONTHS_BACK = 2;
const PANEL_MONTHS_FORWARD = 2;

/** Clave de mes (YYYY-MM) de una fecha. */
function monthKeyOf(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

/** Clave de mes desplazada N meses (admite negativos). */
function shiftMonthKey(monthKey, months) {
  const [year, month] = monthKey.split("-").map(Number);
  return monthKeyOf(new Date(year, month - 1 + months, 1));
}

/**
 * Agrupa claves de mes ordenadas en tramos consecutivos, para pedir un solo rango
 * por tramo en vez de una llamada por mes (y sin traer los meses del medio si el
 * usuario salta de septiembre a diciembre).
 */
function groupConsecutiveMonthKeys(sortedKeys) {
  const runs = [];
  for (const key of sortedKeys) {
    const current = runs[runs.length - 1];
    if (current && shiftMonthKey(current[current.length - 1], 1) === key) {
      current.push(key);
    } else {
      runs.push([key]);
    }
  }
  return runs;
}

export default {
  name: "GoTradingDashboard",
  mixins: [mobileLayoutMixin],
  inject: {
    dashboardLayout: {
      from: "dashboardLayout",
      default: null,
    },
  },
  components: {
    DashboardWidgetGrid,
    AgentAiTopButton: () => import("@/modules/telegram_ai_module/components/AgentAiTopButton.vue"),
    Calendar,
    RecentActivity,
    OperationsSummary,
    ShippingStatusBadge,
    CalendarSync,
    MyItemsButton,
    NotificationPreferencesButton,
    ColumnFieldHelpIcon,
    ContentSkeleton,
  },
  data() {
    return {
      stats: {
        contacts: 0,
        proforms: 0,
        orders: 0,
        products: 0,
      },
      modelData: {}, // Almacena los datos de todos los modelos dinámicamente
      selectedDate: null,
      selectedProforms: [],
      calendarModels: [], // Modelos que tienen campos de fecha para calendario
      /**
       * Modelos del calendario con campos de usuario dueño (`userOwnerFields`), los
       * únicos que «My items» puede filtrar. Si no hay ninguno, el botón no aparece.
       */
      myItemsFilterModels: [],
      /**
       * «My items» prendido (ticket #424). Arranca prendido salvo para admin, que
       * suele querer ver todo; se decide en `mounted` con el rol de la sesión.
       */
      myItemsFilterOn: false,
      calendarEvents: [],
      /** Mes que está mostrando el calendario (para saber qué rango pedir). */
      visibleCalendarDate: new Date(),
      /**
       * Meses (YYYY-MM) ya traídos del endpoint de calendario. Es un `Set` a
       * propósito: no se renderiza, solo evita volver a pedir lo mismo al navegar.
       */
      loadedCalendarMonths: new Set(),
      /** Si el endpoint por rango falla, se vuelve al listado paginado de siempre. */
      calendarRangeEndpointUnavailable: false,
      isCalendarContracted: false, // Control de contracción del calendario
      selectedDayEvents: [], // Eventos del día seleccionado
      highlightedEventId: null, // ID del evento destacado
      /** Fecha del día seleccionado en calendario (para alta Event con query) */
      selectedDayJsDate: null,
      showCalendarSettings: false, // Control del modal de configuración
      calendarPreferences: {}, // Preferencias del usuario para campos del calendario
      tempCalendarPreferences: {}, // Preferencias temporales mientras se edita
      calendarPreferencesLoaded: false, // Flag para indicar que las preferencias ya se cargaron
      loading: {
        models: true,
        calendarEvents: true,
        operationsSummary: true,
      },
    };
  },
  methods: {
    async fetchModels() {
      try {
        this.loading.models = true;
        const { data } = await wencesApi.get(
          "/api/gotrading/model-definitions"
        );

        // Buscar todos los modelos que tienen campos de fecha marcados para calendario
        this.calendarModels = [];
        this.myItemsFilterModels = [];

        const pickDateFields = (modelName, fields, labelPrefix = "") =>
          (fields || [])
            .filter((f) => f && f.type === "date" && f.calendar === true)
            // Ticket #424: en Duntower algunas fechas no se muestran en ningún
            // lado del dashboard, ni siquiera en el engranaje. Todo lo demás
            // (calendario, resumen) sale de esta lista.
            .filter(
              (f) => !this.isDuntower || !isHiddenDashboardField(modelName, f.key)
            )
            .map((f) => ({
              key: f.key,
              label: `${labelPrefix}${f.abbreviation || f.label}`,
            }));

        // `/model-definitions` también lista cada group como modelo propio
        // (`isGroupModel`), y en alisof hay un reporte que se llama igual que un
        // group (`Contrato_POSICIONES`). Las fechas de groups se arman abajo desde el
        // modelo padre, así que esas entradas se saltean para no repetirlas.
        const groupModelNames = new Set();
        data.forEach((model) => {
          (model.fields || []).forEach((f) => {
            if (f && f.type === "group" && !model.isGroupModel) {
              groupModelNames.add(`${model.modelName}_${f.key}`);
            }
          });
        });

        data.forEach((model) => {
          if (!model.fields) return;
          if (model.isGroupModel || groupModelNames.has(model.modelName)) return;
          // Mismo criterio que el botón «My items» del listado.
          const hasOwnerFields =
            resolveValidUserOwnerFieldKeys(model, model.fields).length > 0;

          const calendarDateFields = pickDateFields(model.modelName, model.fields);
          if (calendarDateFields.length > 0) {
            this.calendarModels.push({
              modelName: model.modelName,
              fields: calendarDateFields,
            });
            if (hasOwnerFields) this.myItemsFilterModels.push(model.modelName);
          }

          // Fechas dentro de un group con colección propia (las apropiaciones y
          // facturas del contrato en alisof). Solo las trae el endpoint por rango:
          // el listado dinámico no sabe devolver el padre de cada fila.
          if (!this.usesCalendarRangeEndpoint) return;
          model.fields.forEach((groupField) => {
            if (!groupField || groupField.type !== "group" || groupField.storeInline) return;
            const groupModelName = `${model.modelName}_${groupField.key}`;
            // «Envíos · Fecha de pago» y «Facturas · Fecha de pago» son fechas
            // distintas: sin el grupo, el engranaje y la leyenda las repetirían.
            const groupDateFields = pickDateFields(
              groupModelName,
              groupField.fields,
              `${groupField.label || groupField.key} · `
            );
            if (groupDateFields.length === 0) return;
            this.calendarModels.push({
              modelName: groupModelName,
              displayName: model.label || model.modelName,
              parentModelName: model.modelName,
              fields: groupDateFields,
            });
            if (hasOwnerFields) this.myItemsFilterModels.push(groupModelName);
          });
        });

        // Inicializar preferencias si no hay preferencias guardadas, antes de armar
        // los eventos: si no, la primera pintada se hacía con el mapa vacío.
        this.initializeDefaultPreferences();

        // Cargar datos de todos los modelos que tienen campos de calendario
        await this.fetchAllModelData();
      } catch (error) {
        console.error("Error fetching models:", error);
      } finally {
        this.loading.models = false;
      }
    },
    /**
     * Prende/apaga «My items». Los datos ya cargados son del otro alcance, así que
     * se descartan y se vuelven a pedir; calendario y resumen se
     * actualizan solos porque salen de `modelData`.
     */
    async toggleMyItemsFilter() {
      if (!this.canUseMyItemsFilter) return;
      this.myItemsFilterOn = !this.myItemsFilterOn;
      this.modelData = {};
      this.loadedCalendarMonths = new Set();
      await this.fetchAllModelData();
    },
    async fetchAllModelData() {
      try {
        this.loading.calendarEvents = true;
        if (this.usesCalendarRangeEndpoint) {
          await this.fetchCalendarMonths(this.requiredCalendarMonthKeys());
        } else {
          await this.fetchAllModelDataPaginated();
        }
        this.updateCalendarEvents();
      } catch (error) {
        console.error("Error fetching model data:", error);
      } finally {
        this.loading.calendarEvents = false;
      }
    },
    /**
     * Camino anterior: una llamada al listado dinámico por modelo.
     *
     * Devuelve las 10 filas por defecto del listado (`perPage = 10`), que es lo que
     * dejaba el calendario casi vacío en Duntower. Se mantiene para los tenants que
     * no usan el endpoint por rango y como respaldo si ese endpoint falla.
     */
    async fetchAllModelDataPaginated() {
      for (const calendarModel of this.calendarModels) {
        // Las filas de un group necesitan su padre, que solo arma el endpoint por rango.
        if (calendarModel.parentModelName) continue;
        try {
          // El listado ya entiende `ownerScope=mine` (y lo ignora en modelos sin
          // campos de usuario dueño), así que alcanza con pasarlo.
          const { data } = await wencesApi.get(
            `/api/gotrading/dynamic/${calendarModel.modelName}`,
            { params: this.ownerScopeParams }
          );
          this.$set(this.modelData, calendarModel.modelName, data);
        } catch (error) {
          console.error(
            `Error fetching data for model ${calendarModel.modelName}:`,
            error
          );
          this.$set(this.modelData, calendarModel.modelName, []);
        }
      }
    },
    /**
     * Meses que el dashboard necesita tener cargados: la ventana de los bloques de
     * la derecha (alrededor de hoy) y el mes visible con sus vecinos, porque la
     * grilla dibuja también los días de los meses de al lado.
     */
    requiredCalendarMonthKeys() {
      const keys = new Set();
      const today = new Date();
      for (let i = -PANEL_MONTHS_BACK; i <= PANEL_MONTHS_FORWARD; i += 1) {
        keys.add(monthKeyOf(new Date(today.getFullYear(), today.getMonth() + i, 1)));
      }
      const visible = this.visibleCalendarDate || today;
      for (let i = -1; i <= 1; i += 1) {
        keys.add(
          monthKeyOf(new Date(visible.getFullYear(), visible.getMonth() + i, 1))
        );
      }
      return Array.from(keys);
    },
    /** Trae del back los meses que falten y los acumula en `modelData`. */
    async fetchCalendarMonths(monthKeys) {
      const missing = monthKeys
        .filter((key) => !this.loadedCalendarMonths.has(key))
        .sort();
      if (missing.length === 0) return;

      for (const run of groupConsecutiveMonthKeys(missing)) {
        const from = `${run[0]}-01`;
        const to = `${shiftMonthKey(run[run.length - 1], 1)}-01`;
        try {
          const { data } = await wencesApi.get(
            "/api/gotrading/dashboard/calendar-events",
            { params: { from, to, ...this.ownerScopeParams } }
          );
          if (!data || data.ok !== true || !data.models) {
            throw new Error("Respuesta inesperada del endpoint de calendario");
          }
          this.mergeCalendarModelData(data.models);
          run.forEach((key) => this.loadedCalendarMonths.add(key));
          if (Array.isArray(data.truncated) && data.truncated.length > 0) {
            console.warn(
              `[dashboard] Calendario truncado por volumen en: ${data.truncated.join(", ")}`
            );
          }
        } catch (error) {
          console.error(
            `Error cargando eventos de calendario (${from} → ${to}):`,
            error
          );
          // Sin este respaldo el dashboard quedaría vacío si el endpoint no está
          // disponible (por ejemplo, back sin actualizar).
          this.calendarRangeEndpointUnavailable = true;
          await this.fetchAllModelDataPaginated();
          return;
        }
      }
    },
    /**
     * Suma lo que llegó a `modelData` sin perder lo ya cargado de otros meses.
     * Se deduplica por `_id` porque un documento puede tener fechas en dos meses.
     */
    mergeCalendarModelData(models) {
      Object.keys(models).forEach((modelName) => {
        const incoming = Array.isArray(models[modelName]) ? models[modelName] : [];
        const byId = new Map(
          (this.modelData[modelName] || []).map((item) => [String(item._id), item])
        );
        incoming.forEach((item) => byId.set(String(item._id), item));
        this.$set(this.modelData, modelName, Array.from(byId.values()));
      });
    },
    async fetchProforms() {
      try {
        const { data } = await wencesApi.get("/api/gotrading/dynamic/Proform");
        this.$set(this.modelData, "Proform", data);
        this.updateCalendarEvents();
      } catch (error) {
        console.error("Error fetching proforms:", error);
      }
    },
    async fetchShippings() {
      try {
        const { data } = await wencesApi.get("/api/gotrading/dynamic/Shipping");
        this.$set(this.modelData, "Shipping", data);
        this.updateCalendarEvents();
      } catch (error) {
        console.error("Error fetching shippings:", error);
      }
    },
    /** Texto principal del ítem para el calendario (prioriza `title`, como en modelos Event). */
    resolveCalendarEventTitle(item, modelName) {
      if (!item) return "";
      const s = (v) =>
        v != null && String(v).trim() !== "" ? String(v).trim() : "";
      // Fila de un group: se nombra por su documento padre (el contrato).
      if (item.parent && s(item.parent.name)) return item.parent.name;
      if (s(item.title)) return item.title;
      if (s(item.name)) return item.name;
      if (s(item.label)) return item.label;
      if (item.proform_number)
        return `Proform ${item.proform_number}`;
      if (item.invoice_number)
        return `Invoice ${item.invoice_number}`;
      if (item.collection_number)
        return `Collection ${item.collection_number}`;
      if (item._id && modelName)
        return `${modelName} ${String(item._id).slice(-6)}`;
      return "";
    },
    updateCalendarEvents() {
      this.calendarEvents = [];

      // Procesar todos los modelos dinámicamente
      this.calendarModels.forEach((calendarModel) => {
        const modelData = this.modelData[calendarModel.modelName] || [];

        modelData.forEach((item) => {
          calendarModel.fields.forEach((field) => {
            // Verificar si el campo está habilitado en las preferencias del usuario
            const fieldId = `${calendarModel.modelName}.${field.key}`;
            const isFieldEnabled = this.calendarPreferences[fieldId] !== false; // Por defecto true si no está configurado

            // La ETA trae solo los embarques en rojo («Upcoming ETA»), igual que
            // el reporte que mira el usuario. Ver `FIELD_STATUS_RULES`.
            if (
              this.appliesFieldStatusRules &&
              !matchesFieldStatusRule(calendarModel.modelName, field.key, item)
            ) {
              return;
            }

            // Estado del embarque destildado en el engranaje. Ver `FILTERABLE_SHIPPING_STATUSES`.
            if (
              isShippingStatusHidden(calendarModel.modelName, item, this.calendarPreferences)
            ) {
              return;
            }

            if (item[field.key] && isFieldEnabled) {
              // Obtener información del tipo de evento
              const eventTypeInfo = this.getEventTypeInfo(
                calendarModel.modelName,
                field.key,
                field.label
              );
              
              this.calendarEvents.push({
                _id: item._id,
                title: this.resolveCalendarEventTitle(item, calendarModel.modelName),
                eventDate: item[field.key],
                field: field.label,
                item: item,
                modelName: calendarModel.modelName,
                modelLabel: calendarModel.displayName || calendarModel.modelName,
                // A dónde lleva el clic: la fila de un group abre su documento padre.
                route: calendarEventRoute(calendarModel, item),
                eventType: eventTypeInfo.type,
                eventTypeInfo: eventTypeInfo,
                // Código corto para el chip del calendario, donde no entra la
                // etiqueta completa sin comerse el nombre de la operación.
                fieldShort: eventTypeInfo.shortKey,
                fieldKey: field.key
              });
            }
          });
        });
      });

      if (this.isCalendarContracted && this.selectedDayJsDate) {
        this.syncSelectedDayEventsFromCalendar();
      } else {
        this.showTodayEvents();
      }
    },
    syncSelectedDayEventsFromCalendar() {
      const date = this.selectedDayJsDate;
      if (!date) return;
      this.selectedDate = date.toLocaleDateString(
        this.$i18n.locale === "es" ? "es-ES" : "en-US",
        {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      );
      // Se compara el día civil tal como está guardado (`toYyyyMmDdSync`) contra el
      // día que tocó el usuario: con `new Date(...).toDateString()` una fecha como
      // "2026-11-16" se leía como medianoche UTC y en Argentina caía el día anterior.
      const dayKey = toLocalYyyyMmDd(date);
      this.selectedDayEvents = this.calendarEvents.filter(
        (event) => toYyyyMmDdSync(event.eventDate) === dayKey
      );
    },
    async loadCalendarPreferences() {
      try {
        const { data } = await wencesApi.get("/api/users/calendar-preferences");
        if (data && data.ok && data.data) {
          // Si hay preferencias guardadas, usarlas
          if (data.data.preferences && Object.keys(data.data.preferences).length > 0) {
            this.calendarPreferences = data.data.preferences;
            console.log("Loaded calendar preferences:", this.calendarPreferences);
          } else {
            // Si no hay preferencias, dejar vacío para que se inicialicen por defecto
            this.calendarPreferences = {};
          }
        } else {
          this.calendarPreferences = {};
        }
        this.calendarPreferencesLoaded = true;
      } catch (error) {
        console.error("Error loading calendar preferences:", error);
        // Si no hay preferencias guardadas, dejar vacío para que se inicialicen por defecto
        this.calendarPreferences = {};
        this.calendarPreferencesLoaded = true;
      }
    },
    async saveCalendarPreferences() {
      try {
        const response = await wencesApi.post("/api/users/calendar-preferences", {
          preferences: this.calendarPreferences
        });
        console.log("Saved calendar preferences:", response.data);
      } catch (error) {
        console.error("Error saving calendar preferences:", error);
      }
    },
    openCalendarSettings() {
      // Inicializar preferencias temporales con las actuales o todas en true por defecto
      this.tempCalendarPreferences = {};
      this.availableCalendarFields.forEach((field) => {
        // Si hay preferencia guardada, usarla; si no, por defecto true
        this.$set(
          this.tempCalendarPreferences,
          field.id,
          this.calendarPreferences[field.id] !== false
        );
      });
      this.showCalendarSettings = true;
    },
    applyCalendarSettings() {
      // Aplicar las preferencias temporales a las reales
      this.calendarPreferences = JSON.parse(JSON.stringify(this.tempCalendarPreferences));
      this.saveCalendarPreferences();
      this.updateCalendarEvents(); // Actualizar eventos con las nuevas preferencias
      this.showCalendarSettings = false;
    },
    cancelCalendarSettings() {
      // Restaurar preferencias temporales
      this.tempCalendarPreferences = {};
      this.showCalendarSettings = false;
    },
    handleToggleAllCalendarFields(event) {
      const value = event.target.checked;
      this.availableCalendarFields.forEach((field) => {
        this.$set(this.tempCalendarPreferences, field.id, value);
      });
    },
    initializeDefaultPreferences() {
      // Solo inicializar preferencias por defecto si:
      // 1. Las preferencias ya se cargaron (para evitar race conditions)
      // 2. No hay preferencias guardadas (objeto vacío)
      if (this.calendarPreferencesLoaded && Object.keys(this.calendarPreferences).length === 0) {
        this.calendarModels.forEach((model) => {
          model.fields.forEach((field) => {
            const fieldId = `${model.modelName}.${field.key}`;
            this.$set(this.calendarPreferences, fieldId, true);
          });
        });
        console.log("Initialized default calendar preferences:", this.calendarPreferences);
      }
    },
    showTodayEvents() {
      const today = new Date();
      const todayKey = calendarTodayLocalYyyyMmDd();
      const todayEvents = this.calendarEvents.filter(
        (event) => toYyyyMmDdSync(event.eventDate) === todayKey
      );

      this.selectedDate = today.toLocaleDateString(
        this.$i18n.locale === "es" ? "es-ES" : "en-US",
        {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      );
      this.selectedProforms = todayEvents;
    },
    showProformDetails({ date, events, highlightedEvent }) {
      this.selectedDayJsDate = date ? new Date(date.getTime()) : null;
      this.selectedDate = date.toLocaleDateString(
        this.$i18n.locale === "es" ? "es-ES" : "en-US",
        {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      );
      this.selectedDayEvents = events;
      this.highlightedEventId = highlightedEvent; // Evento destacado
      this.isCalendarContracted = true; // Contraer el calendario
    },
    expandCalendar() {
      this.isCalendarContracted = false;
      this.selectedDayEvents = [];
      this.selectedDate = null;
      this.highlightedEventId = null;
      this.selectedDayJsDate = null;
    },
    isHighlightedCalendarEvent(event) {
      if (this.highlightedEventId == null || !event || event._id == null) {
        return false;
      }
      return String(this.highlightedEventId) === String(event._id);
    },
    formatCalendarEventDateTime(event) {
      if (!event || !event.eventDate) return "";
      const d = new Date(event.eventDate);
      if (Number.isNaN(d.getTime())) return "";
      const locale = this.$i18n.locale === "es" ? "es-ES" : "en-US";
      const item = event.item || {};
      const isAllDay =
        item.allDay === true ||
        item.allDay === "true" ||
        String(item.allDay).toLowerCase() === "true";
      if (String(event.modelName).toLowerCase() === "event" && isAllDay) {
        return d.toLocaleDateString(locale, {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      return d.toLocaleString(locale, {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getColorStatus(status) {
      // Fallback al método original para otros casos
      switch (status.toString()) {
        case "1":
          return "status-grey";
        case "2":
          return "status-yellow";
        case "3":
          return "status-orange";
        case "4":
          return "status-blue";
        case "5":
          return "status-red";
        case "6":
          return "status-green";
        default:
          return "status-grey";
      }
    },

    /**
     * Al cambiar de mes se traen los meses que falten.
     *
     * A propósito no se toca `loading.calendarEvents`: ese flag reemplaza el
     * calendario por el spinner, y al volver a montarse el componente arrancaría
     * de nuevo en el mes actual, sacando al usuario del mes que estaba mirando.
     * La carga es silenciosa y los chips aparecen cuando llegan los datos.
     */
    async handleMonthChange(date) {
      this.visibleCalendarDate = date ? new Date(date.getTime()) : new Date();
      if (!this.usesCalendarRangeEndpoint) return;

      const monthKeys = this.requiredCalendarMonthKeys();
      if (monthKeys.every((key) => this.loadedCalendarMonths.has(key))) return;

      await this.fetchCalendarMonths(monthKeys);
      this.updateCalendarEvents();
    },
    viewItem(event) {
      this.$router.push(event.route);
    },
    /**
     * Ícono, color, prioridad y tipo de una fecha del calendario.
     *
     * Sale de `utils/dashboardEventTypes.js`, que es la única fuente de esta
     * semántica: antes estaba acá en un `eventTypeConfig` propio y repetida en
     * `EventsAnalyzer` y `OperationsSummary` con distintas claves de campo (las
     * fechas de cut off de Duntower, `cod` y `cop`, no figuraban en ninguna, así que
     * salían en gris como «Cod» y «Cop»).
     *
     * El texto visible del tipo se traduce acá y no en el mapa: en el mapa quedan
     * claves estables, y `this.$t` en `data()` se evaluaba una sola vez al crear el
     * componente, antes de que el usuario pudiera cambiar de idioma.
     */
    getEventTypeInfo(modelName, fieldKey, fieldLabel) {
      const info = resolveEventTypeInfo(modelName, fieldKey, fieldLabel);
      return {
        ...info,
        type: this.$t(info.typeKey),
        label: this.$t(info.label),
      };
    },

    /**
     * Clase del badge del tipo de evento.
     *
     * Viene de los metadatos y no de un `switch` sobre el texto: ese switch
     * comparaba `event.eventType` (ya traducido) contra literales en inglés, así que
     * en español no acertaba ningún caso y todo caía en el default.
     */
    getEventTypeBadgeClass(event) {
      const info = event && event.eventTypeInfo;
      const badgeClass = (info && info.badgeClass) || "badge-secondary";
      // Ticket #424: el badge del tipo (ETA, ETD…) de un embarque ya cobrado lleva
      // el texto en verde, igual que el chip del calendario y el badge de status.
      // La clase de status hace falta para que el CSS elija el verde según el fondo.
      if (this.isShippingStatusEvent(event) && isShippingClientPendingSettled(event.item)) {
        return `${badgeClass} ${getShippingStatusClass(event.item)} ${SHIPPING_COLLECTED_CLASS}`;
      }
      return badgeClass;
    },

    getEventTypeIcon(eventTypeInfo) {
      return eventTypeInfo.icon || "fas fa-calendar";
    },

    getEventTypeColor(eventTypeInfo) {
      return eventTypeInfo.color || "#6c757d";
    },

    /**
     * Color del evento en el panel del día.
     *
     * Los embarques se pintan con su status operativo, la misma paleta que el badge
     * del listado/reporte. Es el motivo del ticket #365: `eventTypeConfig` tiene el
     * mismo celeste para todos los tipos, así que un ETA de una operación en
     * «Upcoming ETA» se veía celeste acá y rojo en el reporte.
     *
     * Para el resto de los modelos (pagos, cobros, eventos) se mantiene el color por
     * tipo de evento de siempre.
     */
    getEventColor(event) {
      if (this.isShippingStatusEvent(event)) {
        return getShippingStatusColor(event.item);
      }
      return this.getEventTypeColor(event.eventTypeInfo);
    },

    /** Badge del tipo de evento con el color del status, para embarques en Duntower. */
    getEventBadgeStyle(event) {
      if (!this.isShippingStatusEvent(event)) return {};
      const colors = getShippingStatusColors(event.item);
      return { backgroundColor: colors.background, color: colors.text };
    },

    /** Evento de embarque que se pinta por status (solo donde está habilitado). */
    isShippingStatusEvent(event) {
      return (
        this.usesShippingStatusColors &&
        !!event &&
        !!event.item &&
        String(event.modelName || "").toLowerCase() === "shipping"
      );
    },

    // Utility methods for event display
    formatNumber(number) {
      if (!number) return this.$t("0.00");
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(number);
    },


    getProformStatusClass(status) {
      switch (status) {
        case "draft":
          return "text-secondary";
        case "pending":
          return "text-warning";
        case "approved":
          return "text-success";
        case "rejected":
          return "text-danger";
        default:
          return "text-muted";
      }
    },

    getProformStatusLabel(status) {
      switch (status) {
        case "draft":
          return this.$t("Draft");
        case "pending":
          return this.$t("Pending");
        case "approved":
          return this.$t("Approved");
        case "rejected":
          return this.$t("Rejected");
        default:
          return this.$t("Unknown");
      }
    },
  },
  async mounted() {
    // Inicializar estados de loading
    this.loading = {
      models: true,
      calendarEvents: true,
      operationsSummary: true,
    };

    // «My items» arranca prendido salvo para admin (ticket #424). Mismo criterio de
    // rol que el layout (`DashboardLayout.isAdminRole`).
    this.myItemsFilterOn = !this.isAdminRole;

    // Cargar preferencias primero antes de inicializar modelos
    await this.loadCalendarPreferences();
    this.fetchModels();

    // Simular loading para operations summary
    setTimeout(() => {
      this.loading.operationsSummary = false;
    }, 1500);
  },
  computed: {
    /**
     * Los cambios del ticket #365 (calendario por rango y chips pintados con el
     * status del embarque) se pidieron para Duntower. El resto de los tenants
     * mantiene el comportamiento anterior: no pidieron el cambio y no tiene sentido
     * moverles el dashboard de forma indirecta.
     */
    isDuntower() {
      return isDuntowerTenant();
    },
    /** Mismo criterio que `DashboardLayout.isAdminRole`: el rol de la sesión. */
    isAdminRole() {
      if (this.dashboardLayout && typeof this.dashboardLayout.isAdminRole === "boolean") {
        return this.dashboardLayout.isAdminRole;
      }
      try {
        return localStorage.getItem("role") === "admin";
      } catch (e) {
        return false;
      }
    },
    /** Trae el calendario por rango de fechas en vez del listado paginado. */
    usesCalendarRangeEndpoint() {
      return usesCalendarEndpointTenant() && !this.calendarRangeEndpointUnavailable;
    },
    /** Hay algún modelo del calendario que «My items» pueda filtrar. */
    canUseMyItemsFilter() {
      return this.myItemsFilterModels.length > 0;
    },
    isMyItemsFilterActive() {
      return this.canUseMyItemsFilter && this.myItemsFilterOn;
    },
    /** Parámetros para pedir solo lo del usuario, igual que el listado. */
    ownerScopeParams() {
      return this.isMyItemsFilterActive ? { ownerScope: "mine" } : {};
    },
    /** Pinta los eventos de embarque con el color de su status operativo. */
    usesShippingStatusColors() {
      return this.isDuntower;
    },
    /**
     * Aplica las reglas de status por campo: hoy, que la ETA traiga únicamente los
     * embarques en rojo («Upcoming ETA»), que es lo que se ve en el reporte.
     */
    appliesFieldStatusRules() {
      return this.isDuntower;
    },
    showJurisprudenciaDashboardShortcuts() {
      const d = this.dashboardLayout;
      if (!d || !d.isJurisprudenciaModuleActive) return false;
      return d.canAccessJurisprudenciaModule || d.canAccessMevModule;
    },
    jurisprudenciaShortcutVisible() {
      const d = this.dashboardLayout;
      return !!(d && d.canAccessJurisprudenciaModule);
    },
    mevShortcutVisible() {
      const d = this.dashboardLayout;
      return !!(d && d.canAccessMevModule);
    },
    availableCalendarFields() {
      // Generar lista de campos disponibles para el calendario
      const fields = [];
      this.calendarModels.forEach((model) => {
        model.fields.forEach((field) => {
          const fieldId = `${model.modelName}.${field.key}`;
          fields.push({
            id: fieldId,
            modelName: model.modelName,
            modelLabel: model.displayName || model.modelName,
            fieldKey: field.key,
            label: field.label,
            // Ayuda (?) del ítem: solo en Duntower, sus textos describen sus reglas.
            helpKey: this.isDuntower
              ? dashboardFieldHelpKey(model.modelName, field.key)
              : "",
          });
        });
        // En Duntower, además de las fechas, se pueden ocultar los estados de
        // embarque que no tienen una fecha propia (ver `FILTERABLE_SHIPPING_STATUSES`).
        // Van a continuación de las fechas de shipping, para que queden juntos.
        if (this.isDuntower && String(model.modelName || "").toLowerCase() === "shipping") {
          FILTERABLE_SHIPPING_STATUSES.forEach((status) => {
            fields.push({
              id: shippingStatusPreferenceId(status),
              modelName: model.modelName,
              label: this.$t(getStatusLabel(status)),
              helpKey: shippingStatusHelpKey(status),
            });
          });
        }
      });
      return fields;
    },
    areAllCalendarFieldsSelected() {
      if (this.availableCalendarFields.length === 0) return false;
      return this.availableCalendarFields.every(
        field => this.tempCalendarPreferences[field.id] === true
      );
    },
    selectedCalendarFieldsCount() {
      return Object.values(this.tempCalendarPreferences).filter(v => v === true).length;
    },
    highlightedEventDateTime() {
      if (!this.highlightedEventId || !this.selectedDayEvents?.length) return "";
      const hid = String(this.highlightedEventId);
      const ev = this.selectedDayEvents.find((e) => String(e._id) === hid);
      return ev ? this.formatCalendarEventDateTime(ev) : "";
    },
  },
};
</script>

<style scoped>
/* Filas de la configuración de campos: mismo diseño que «Mis notificaciones». */
.cal-field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  margin: 0;
  border-bottom: 1px solid var(--bs-border-color, #dee2e6);
  cursor: pointer;
}
.cal-field-row--all {
  border-bottom-width: 2px;
}
.cal-field-row .form-check-input {
  cursor: pointer;
}
/* Ayuda (?) de los ítems del engranaje: siempre a la vista (en las columnas de los
   listados solo aparece al pasar el mouse) y con el color del texto, así se lee
   igual en modo claro y oscuro. */
.calendar-field-help ::v-deep .column-field-help-btn {
  opacity: 0.6;
  color: inherit;
}

.calendar-field-help ::v-deep .column-field-help-btn:hover,
.calendar-field-help ::v-deep .column-field-help-btn:focus {
  opacity: 1;
  color: inherit;
}

.events-empty-state {
  background-color: #eef0f3;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.event-item--highlighted {
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.45);
  border-radius: 8px;
  padding: 10px;
  margin-left: -4px;
  margin-right: -4px;
  background-color: rgba(13, 110, 253, 0.04);
}

.calendar-event-datetime {
  display: flex;
  align-items: center;
}

.card {
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-5px);
}

/* Layout dinámico para el dashboard */
.dashboard-layout {
  display: flex;
  transition: all 0.3s ease;
  min-height: 600px;
}

/* Sección de lista de eventos (a la izquierda) */
.events-list-section {
  flex: 0 0 25%; /* Ancho fijo del 25% */
  transition: all 0.3s ease;
  padding-right: 15px;
  min-height: 600px;
  animation: slideInLeft 0.3s ease;
  transform: translateX(0);
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Transiciones de Vue para el listado de eventos */
.slide-left-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-left-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-left-enter {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-left-enter-to,
.slide-left-leave {
  transform: translateX(0);
  opacity: 1;
}

/* Estilo para evento destacado */
.highlighted-event {
  border-left: 6px solid #2196f3 !important;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2) !important;
  transform: scale(1.01);
  animation: highlightPulse 0.6s ease-out;
  position: relative;
}

.event-item .event-header .event-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.event-item .event-meta {
  font-size: 0.8125rem;
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
}

.event-item .event-meta .row {
  margin-bottom: 0.25rem;
}

.event-item .event-meta strong {
  font-weight: 600;
  color: #495057;
}

.event-item .event-meta .text-primary {
  color: #007bff !important;
  font-weight: 500;
}

.event-item .event-meta .text-success {
  color: #28a745 !important;
  font-weight: 500;
}

.event-item .event-meta .text-danger {
  color: #dc3545 !important;
  font-weight: 500;
}

.event-item .event-meta .text-warning {
  color: #ffc107 !important;
  font-weight: 500;
}

.event-item .event-meta .text-info {
  color: #17a2b8 !important;
  font-weight: 500;
}

.event-item .event-meta .text-muted {
  color: #6c757d !important;
}

.event-item .badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.event-item .event-actions {
  padding: 0 1rem 1rem 1rem;
  border-top: 1px solid #e9ecef;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}

.event-item .clickable {
  cursor: pointer;
  transition: color 0.2s ease;
}

.event-item .clickable:hover {
  color: #007bff !important;
  text-decoration: underline;
}

.highlighted-event::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(33, 150, 243, 0.05);
  border-radius: 7px;
  pointer-events: none;
}

@keyframes highlightPulse {
  0% {
    transform: scale(1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  50% {
    transform: scale(1.03);
    box-shadow: 0 6px 16px rgba(33, 150, 243, 0.3);
  }
  100% {
    transform: scale(1.01);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
  }
}

.calendar-section {
  flex: 1; /* Toma el espacio restante */
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding-right: 15px;
}

.calendar-section.contracted {
  flex: 1; /* Mantiene el espacio restante */
  padding-right: 15px;
  transform: scale(0.98);
  opacity: 0.9;
}

.events-section {
  flex: 0 0 300px; /* Ancho fijo para la sección derecha */
  transition: all 0.3s ease;
  /* La actividad reciente se estira hasta el pie del calendario. */
  display: flex;
  flex-direction: column;
  position: relative;
}

/*
 * Absoluta para que la lista no estire la fila: el alto lo marca el calendario y
 * lo que no entra se scrollea dentro del bloque.
 */
.events-section__activity {
  position: absolute;
  inset: 0;
}

.events-list-container {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  height: 100%;
  overflow-y: auto;
}

.events-list-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e9ecef;
}

.events-count {
  text-align: center;
}

.events-list {
  max-height: 500px;
  overflow-y: auto;
}

.event-item {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 14px;
  margin-bottom: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  border-left: 4px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.event-item:hover {
  background: #f8f9fa;
  transform: translateX(5px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  border-color: #dee2e6;
}

.event-item:last-child {
  margin-bottom: 0;
}

.event-content {
  flex: 1;
  min-width: 0;
}

.event-header {
  margin-bottom: 8px;
}

.event-title {
  color: #333;
  font-weight: 600;
  margin-bottom: 4px;
  word-break: break-word;
}

.event-meta {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

/* Estilos para badges de tipo de evento */
.badge {
  font-size: 0.75em;
  font-weight: 500;
  padding: 0.25em 0.5em;
  border-radius: 0.375rem;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-info {
  background-color: #0dcaf0;
  color: #000;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

/* Fila título + badge: una línea, título puede envolver */
.event-header .d-flex {
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
}

.event-header .d-flex .event-title {
  flex: 1 1 auto;
  min-width: 0;
}

.event-header .d-flex .badge {
  flex-shrink: 0;
  align-self: flex-start;
}

.event-title.clickable {
  cursor: pointer;
  transition: color 0.2s ease;
}

.event-title.clickable:hover {
  color: #007bff;
}

.event-description {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e0e0e0;
}

.event-actions {
  margin-left: 15px;
  flex-shrink: 0;
}

/* Estilos para los status de eventos */
.status-grey {
  border-left-color: #999 !important;
}

.status-yellow {
  border-left-color: #ffc107 !important;
}

.status-orange {
  border-left-color: #fd7e14 !important;
}

.status-blue {
  border-left-color: #007bff !important;
}

.status-red {
  border-left-color: #dc3545 !important;
}

.status-green {
  border-left-color: #4ddf4d !important;
}

/* Estilos para eventos de payment y collect en el listado */
.list-event-payment {
  background-color: white !important;
  border-left-color: #dc3545 !important;
  border-left-width: 4px !important;
}

.list-event-collect {
  background-color: white !important;
  border-left-color: #28a745 !important;
  border-left-width: 4px !important;
}

/* Efecto hover: cambiar proporciones */
.events-section:hover .calendar-section {
  flex: 0.7; /* Reducir el calendario al 70% */
}

.events-section:hover {
  flex: 0 0 400px; /* Aumentar la sección derecha a 400px */
}

/* Asegurar que el contenido no se rompa */
.calendar-section,
.events-section {
  min-width: 0; /* Permite que el contenido se achique */
}

/* Estilos para el header del calendario */
.calendar-header {
  padding: 0.5rem;
}

/* Responsive layout para móvil */
@media (max-width: 768px) {
  .dashboard-layout {
    flex-direction: column;
    gap: 20px;
  }

  .dashboard-layout.calendar-contracted {
    flex-direction: column;
  }

  .events-list-section {
    flex: none;
    width: 100%;
    padding-right: 0;
    min-height: auto;
    order: 1; /* Aparece primero en móvil */
  }

  /* Transiciones móviles - slide desde arriba */
  .slide-left-enter {
    transform: translateY(-100%);
    opacity: 0;
  }

  .slide-left-leave-to {
    transform: translateY(-100%);
    opacity: 0;
  }

  .calendar-section {
    flex: none;
    padding-right: 0;
    width: 100%;
    order: 2; /* Aparece segundo en móvil */
  }

  .calendar-section.contracted {
    flex: none;
    width: 100%;
    max-height: 50vh; /* Altura máxima del 50% en móvil */
    overflow: hidden;
  }

  .events-section {
    flex: none;
    width: 100%;
    order: 3; /* Aparece tercero en móvil */
  }

  .events-section__activity {
    position: static;
    max-height: 22rem;
  }

  .events-list-container {
    min-height: 300px;
  }

  .events-list-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .event-item {
    flex-direction: column;
    align-items: stretch;
  }

  .event-actions {
    margin-left: 0;
    margin-top: 12px;
  }

  .event-actions .btn {
    width: 100%;
  }

  /* Ocultar el efecto hover en móvil */
  .events-section:hover .calendar-section {
    flex: none;
  }

  .events-section:hover {
    flex: none;
  }
}

.jurisprudencia-dashboard-shortcuts .dashboard-shortcut-card {
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.jurisprudencia-dashboard-shortcuts .dashboard-shortcut-card:hover {
  box-shadow: 0 0.35rem 1rem rgba(0, 0, 0, 0.12) !important;
  transform: translateY(-2px);
}

.jurisprudencia-dashboard-shortcuts .dashboard-shortcut-icon {
  width: 52px;
  height: 52px;
  background: rgba(13, 110, 253, 0.08);
}

.material-icons {
  vertical-align: middle;
}

.badge {
  font-size: 0.8em;
  padding: 0.5em 0.8em;
}

.modal-content {
  border-radius: 8px;
}

.list-group-item {
  border-left: none;
  border-right: none;
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:last-child {
  border-bottom: none;
}

/*
 * Los bloques de siempre viven dentro de un widget (DashboardWidgetGrid): el marco ya
 * es la tarjeta, así que acá van sin márgenes de fila ni borde propio.
 */
.dashboard-layout {
  margin: 0;
  min-height: 0;
  padding: 8px;
}
/* Dentro del widget no hace falta el relleno de la sección ni la sombra de la tarjeta
   propia del calendario: el marco del widget ya separa (con la lista del día abierta,
   el espacio entre las dos lo pone la lista). */
.dashboard-layout .calendar-section {
  padding: 0 !important;
}
.dashboard-layout ::v-deep .desktop-calendar {
  box-shadow: none !important;
}
.dashboard-recent-activity {
  height: 100%;
  border: 0 !important;
  box-shadow: none !important;
  border-radius: 0;
}
.dashboard-recent-activity ::v-deep .recent-activity__head {
  display: none !important;
}
/* El resumen trae su propia tarjeta con título: dentro del widget sobra (el marco ya
   dice «Resumen de operaciones»). */
.dashboard-operations-summary ::v-deep > .card {
  border: 0 !important;
  box-shadow: none !important;
  margin: 0 !important;
  background: transparent !important;
}
.dashboard-operations-summary ::v-deep > .card > .card-header {
  display: none !important;
}
.dashboard-operations-summary ::v-deep > .card > .card-body {
  padding: 10px 12px !important;
}
.jurisprudencia-dashboard-shortcuts {
  margin: 0 !important;
  padding: 10px 4px;
}
</style>
